import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { EASINGS } from "../common/Easing";
import { getScrollParent, listen } from "../common/Dom";
import { getOtherProperties, getSize } from "../common/Utils";
import { BackTopProps, BackTopState } from "./PropsType";

export default function BackTop(props: BackTopProps) {
  const {
    className,
    children,
    destination = 0,
    duration = 200,
    right = 30,
    bottom = 30,
    easing = "linear",
    prefixCls = "bm-BackTop",
    ...other
  } = props;
  const [visible, setVisible] = useState(false);
  const styleClass = classNames(prefixCls, className);
  const styles = {
    right: getSize(right),
    bottom: getSize(bottom),
    display: visible ? "block" : "none",
  };
  const otherProps = getOtherProperties(other, [
    "destination",
    "duration",
    "easing",
    "scrollNode",
    "onScrollEnd",
  ]);
  let startTime: number = 0;
  let startY: number = 0;
  const node = useRef(null);
  let scrollListener: any;
  const scrollNode = useRef<any>(null);
  let cancelRequestAnimate: number | null = null;

  const scroll = (e) => {
    if (cancelRequestAnimate) {
      window.cancelAnimationFrame(cancelRequestAnimate);
      cancelRequestAnimate = null;
    }
    const { destination, duration, easing, onScrollEnd }: any = props;
    const now = Date.now();
    const time = Math.min(1, (now - startTime) / duration);
    const timeFunction = EASINGS[easing](time);
    const scrollTop = timeFunction * (destination - startY) + startY;
    if (e === window) {
      window.scrollTo(0, scrollTop);
    } else {
      e.scrollTop = scrollTop;
    }
    if (scrollTop === destination) {
      if (cancelRequestAnimate) {
        window.cancelAnimationFrame(cancelRequestAnimate);
        cancelRequestAnimate = null;
      }
      if (onScrollEnd) {
        onScrollEnd();
      }
      return;
    }
    cancelRequestAnimate = window.requestAnimationFrame(scroll);
  };

  const handleClick = () => {
    startTime = Date.now();
    if (scrollNode.current) {
      startY = scrollNode.current?.scrollTop || 0;
      if (scrollNode.current === window) {
        startY = document.documentElement.scrollTop || document.body.scrollTop;
      }
      scroll(scrollNode);
    }
  };

  useEffect(() => {
    if (props?.scrollNode) {
      scrollNode.current = props.scrollNode;
    } else {
      scrollNode.current = getScrollParent(node.current);
    }
    if (props.scrollNode?.nodeType === 9) {
      scrollNode.current = window;
    }
    scrollListener = listen(scrollNode.current, "scroll", (e) => {
      const scrollTop =
        e.target.scrollTop ||
        document.documentElement.scrollTop ||
        document.body.scrollTop;
      if (scrollTop > props.destination!) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    });

    return () => {
      if (scrollListener) {
        scrollListener();
        scrollListener = null;
      }
    };
  }, []);

  return (
    <div
      className={styleClass}
      style={styles}
      ref={node}
      {...otherProps}
      onClick={handleClick}
    >
      {children}
    </div>
  );
}
